import { useEffect, useState } from "react";
import { FiSearch } from "react-icons/fi";
import { RiAlignItemLeftFill } from "react-icons/ri";
import { BsExclamationCircle } from "react-icons/bs";
import { TiArrowSortedDown, TiArrowSortedUp } from "react-icons/ti";
import { FaArrowsRotate, FaDownload, FaRotate } from "react-icons/fa6";
import { Badge, Button, Form, ProgressBar, Table } from "react-bootstrap";
import SplitPane from "react-split-pane";
import SecurityAuditGraph from "./SecurityAuditGraph";
import UseToggle from "../../Hooks/UseToggle";
import ViewReport from "./ViewReport";
import { secureScanServices } from "../../APIServices/PasswordManagement/secureScanServices";
import { itemServices } from "../../APIServices/PasswordManagement/itemServices";
import { calculateReuseMap, decryptItemKey, decryptPayload, evaluatePassword, getKeyFromItem, getPasswordFromItem } from "../../APIHelpers/utils";
import { useApp } from "../../APIHelpers/AppContext";

const SecureScan = ({ auth, isDark, currentUserData }) => {
  const toggles = UseToggle()
  const [searchData, setSearchData] = useState("");
  const [activeItem, setActiveItem] = useState("all");
  const [loading, setLoading] = useState(false);
  const [scannedDataListCount, setScannedDataListCount] = useState(0);
  const [scannedDataList, setScannedDataList] = useState([]);
  const [totlalCalculate, setTotlalCalculate] = useState(0);
  const [scannedReport, setScannedReport] = useState("");
  const [selectedRowId, setSelectedRowId] = useState(null);

  const [pageNumber, setPageNumber] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(15);
  const startIndex = (pageNumber - 1) * itemsPerPage;
  const totalPage = Math.ceil(scannedDataListCount / itemsPerPage);

  const { getVaultKey } = useApp();
  const getMyItemList = async () => {
    setLoading(true);
    let data = await itemServices.getMyItemList(auth)
    let result = data?.data || []
    if (result?.length > 0) {
      const finalData = await Promise.all(
        result.map(async (element) => {
          let decryptedItemKey = await decryptItemKey(element?.itemKey, element?.itemKeyIV, getVaultKey())
          let itemInfo = await decryptPayload(element?.encryptedData, element?.encryptedDataIV, decryptedItemKey)
          let objectData = {
            itemInfo: itemInfo,
            metadata: element?.metadata,
            itemId: element?.itemId,
            categoryId: element?.categoryId,
            createdAt: element?.createdAt,
            updatedAt: element?.updatedAt,
            enableTwoFA: element?.enableTwoFA,
          }
          return objectData
        })
      );
      let scanned = analyzeItemsOnly(finalData)
      let data = await secureScanServices.refreshSecureScan(auth, { scanned })
      if (data?.statusCode === 200) {
        getTotalScannedList();
      }
      setTimeout(() => {
        setLoading(false);
      }, 3000);
    }
  }

  function analyzeItemsOnly(items = []) {
    const reuseMap = calculateReuseMap(items);

    return items.map(item => {
      const password = getPasswordFromItem(item);
      const USER_ID_EMAIL = getKeyFromItem(item, "USER_ID_EMAIL");
      
      const reuseCount = reuseMap[password] || 1;

      const pwdReport = evaluatePassword(password);
      const issues = [...pwdReport.issues];

      let risk = 100 - pwdReport.score;

      if (reuseCount > 1) {
        risk += reuseCount >= 3 ? 20 : 10;
        issues.push("Password reused");
      }

      if (!item.enableTwoFA) {
        risk += 10;
        issues.push("2FA not enabled");
      }

      risk = Math.max(0, Math.min(100, risk));

      return {
        adminId: currentUserData.userId,
        accountId: currentUserData.loginAccountId || currentUserData.userId,
        itemId: item.itemId,
        title: item.metadata?.title || "",
        icon: item.metadata?.icon || "",
        info: item.categoryId === "credentials" ? USER_ID_EMAIL : "",
        infoTitle: item.categoryId === "credentials" ? "User / Email Id" : "",
        passwordScore: pwdReport.score,
        strength: pwdReport.strength,
        reuseCount,
        risk,
        issues,
      };
    });
  }

  console.log(">>setScannedResult", scannedDataList);

  const getTotalScannedList = async () => {
    let data = await secureScanServices.getTotalScannedList(auth, searchData, pageNumber, itemsPerPage)
    setScannedDataList(data?.data || [])
    setScannedDataListCount(data?.count || 0)
    setTotlalCalculate(data?.calculate || "")
  }

  const getSingleItemScanReport = async (id) => {
    let data = await secureScanServices.getSingleItemScanReport(auth, id)
    setScannedReport(data?.data?.report || "")
  }

  useEffect(() => {
    getTotalScannedList()
  }, [auth, searchData, pageNumber, itemsPerPage]);

  const getColorByValue = (score) => {
    if (score <= 20) return "#dc3545";
    if (score <= 40) return "#ffc107b5";
    if (score <= 60) return "#ffc107";
    if (score <= 80) return "#fd7e14";
    return "#198754";
  };

  function timeAgo(dateString) {
    const past = new Date(dateString);
    const now = new Date();

    let diffInSeconds = Math.floor((now - past) / 1000);

    if (diffInSeconds <= 0) return "just now";

    const units = [
      { label: "year", value: 31536000 },
      { label: "month", value: 2592000 },
      { label: "day", value: 86400 },
      { label: "hour", value: 3600 },
      { label: "minute", value: 60 },
      { label: "second", value: 1 }
    ];

    for (const unit of units) {
      const count = Math.floor(diffInSeconds / unit.value);
      if (count > 0) {
        return `${count} ${unit.label}${count > 1 ? "s" : ""} ago`;
      }
    }
  }

  return (
    <>
      <div className="position-relative">
        <div className="p-2 hstack gap-2 border-bottom">
          <div className="position-relative w-100">
            <Form.Control
              onChange={(e) => setSearchData(e.target.value)}
              value={searchData}
              type="search"
              className="interactive-input rounded-1 py-2 ps-5"
              title="Search..."
              style={{ fontSize: 12 }}
              placeholder={`Search...`}
            />
            <FiSearch
              className="position-absolute"
              style={{ top: 10, left: 18 }}
            />
          </div>
          <Button className="text-nowrap hstack gap-2 btn-gradient" style={{ fontSize: 13 }} onClick={getMyItemList} disabled={loading} title="Refresh"><FaRotate className={loading ? "rotate-infinite" : ""} /> {loading ? "Scanning..." : "Scan Now"}</Button>
          <Button className="text-nowrap hstack gap-2 btn-gradient" style={{ fontSize: 13 }} title="Download report"><FaDownload /> Download Report</Button>
        </div>
        <SplitPane
          split="vertical"
          minSize={"25%"}
          className="position-relative"
        >
          <div style={{ height: "100vh" }}>
            <SecurityAuditGraph report={totlalCalculate?.watchtowerScore || 0} />
            <aside className="sidebar-container" style={{ height: "calc(100vh - 180px" }}>
              <div className="sidebar-list">
                <div className={`sidebar-item hstack justify-content-between ${activeItem === "all" ? "active" : ""}`} onClick={() => setActiveItem("all")}>
                  <span className="hstack gap-2">
                    <RiAlignItemLeftFill /> All
                  </span>
                  <Badge className="rounded-1">{totlalCalculate?.summary?.totalItems || 0}</Badge>
                </div>
                <div className={`sidebar-item hstack justify-content-between ${activeItem === "reused" ? "active" : ""}`} onClick={() => setActiveItem("reused")}>
                  <span className="hstack gap-2">
                    <FaArrowsRotate /> Reused
                  </span>
                  <Badge className="rounded-1">{totlalCalculate?.summary?.reusedPasswords || 0}</Badge>
                </div>
                <div className={`sidebar-item hstack justify-content-between ${activeItem === "week" ? "active" : ""}`} onClick={() => setActiveItem("week")}>
                  <span className="hstack gap-2">
                    <BsExclamationCircle />
                    Week
                  </span>
                  <Badge className="rounded-1">{totlalCalculate?.summary?.weakPasswords || 0}</Badge>
                </div>
              </div>
            </aside>
          </div>
          <div style={{ height: "100vh" }}>
            {activeItem === "all" && (
              <div className="overflow-auto" style={{ height: "calc(100vh - 54px" }}>
                {scannedDataList?.length > 0 ? (
                  <Table hover className="m-0" style={{ verticalAlign: "middle" }}>
                    <thead className="position-sticky top-0">
                      <tr>
                        <th className="CP" title="Click to Sort" style={{ width: "30%", fontSize: 14 }}>Name <TiArrowSortedDown /></th>
                        <th className="CP" title="Click to Sort" style={{ fontSize: 14 }}>Password Strength <TiArrowSortedUp /></th>
                        <th className="CP text-center" title="Click to Sort" style={{ width: "15%", fontSize: 14 }}>Last Change <TiArrowSortedDown /></th>
                      </tr>
                    </thead>
                    <tbody>
                      {scannedDataList.map((item) => (
                        <tr key={item.itemId} title="View Report" className={`CP ${selectedRowId === item._id ? "selected-row" : ""}`} onClick={() => { toggles?.setToggle(); getSingleItemScanReport(item?._id); setSelectedRowId(item._id); }}>
                          <td>
                            <div className="hstack gap-2">
                              <div className={`${isDark ? "bg-dark" : "bg-white"} rounded-circle p-2 h-100`}>
                                <img
                                  src={item.icon}
                                  alt=""
                                  className="item-icon hstack justify-content-center"
                                />
                              </div>
                              <div className="item-content">
                                <div className="item-title">{item.title}</div>
                                {item.info && (
                                  <div className="item-secondary" title={item.infoTitle}>
                                    {item.info}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td>
                            <ProgressBar
                              now={item?.passwordScore}
                              style={{
                                height: 8,
                                backgroundColor: "#e9ecef"
                              }}
                            >
                              <ProgressBar
                                now={item?.passwordScore}
                                style={{
                                  backgroundColor: getColorByValue(item?.passwordScore)
                                }}
                              />
                            </ProgressBar>
                          </td>
                          <td className="item-title text-center">{timeAgo(item?.updatedAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <div className="item-empty-state">
                    There are no items.
                  </div>
                )}
              </div>
            )}
            {activeItem === "reused" && (
              <div className="overfolw-auto" style={{ height: "calc(100vh - 54px" }}>reused</div>
            )}
            {activeItem === "week" && (
              <div className="overfolw-auto" style={{ height: "calc(100vh - 54px" }}>week</div>
            )}
          </div>
        </SplitPane>
      </div>
      <ViewReport
        Show={!toggles.toggle}
        Hide={toggles.setToggle}
        isDark={isDark}
        Title={`Report Details`}
        report={scannedReport}
      />
    </>
  );
};

export default SecureScan;
